/**
 * FilterPanel — Relationship type checkboxes + weight threshold slider.
 */

import React from 'react';
import { RELATIONSHIP_TYPES, type RelationshipType } from './constants';

interface Props {
  activeTypes: Set<RelationshipType>;
  onToggleType: (type: RelationshipType) => void;
  weightThreshold: number;
  onWeightChange: (value: number) => void;
  criticalOnly: boolean;
  onCriticalOnlyChange: (value: boolean) => void;
}

const FilterPanel: React.FC<Props> = ({
  activeTypes,
  onToggleType,
  weightThreshold,
  onWeightChange,
  criticalOnly,
  onCriticalOnlyChange,
}) => {
  return (
    <div
      style={{
        background: 'var(--bg-secondary, rgba(30,41,59,0.8))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.1))',
        borderRadius: '12px',
        padding: '16px',
        minWidth: '200px',
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #e2e8f0)', marginBottom: '12px' }}>
        Filters
      </div>

      {/* Relationship type checkboxes */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Relationship Types
        </div>
        {(Object.keys(RELATIONSHIP_TYPES) as RelationshipType[]).map((type) => {
          const meta = RELATIONSHIP_TYPES[type];
          return (
            <label
              key={type}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 0',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--text-primary, #e2e8f0)',
              }}
            >
              <input
                type="checkbox"
                checked={activeTypes.has(type)}
                onChange={() => onToggleType(type)}
                style={{ accentColor: meta.color }}
              />
              <span
                style={{
                  width: '12px',
                  height: '3px',
                  background: meta.color,
                  borderRadius: '2px',
                  display: 'inline-block',
                }}
              />
              {meta.label}
            </label>
          );
        })}
      </div>

      {/* Weight threshold */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Min Weight: {weightThreshold}
        </div>
        <input
          type="range"
          min={1}
          max={10}
          value={weightThreshold}
          onChange={(e) => onWeightChange(Number(e.target.value))}
          style={{ width: '100%' }}
        />
      </div>

      {/* Critical only */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          fontSize: '13px',
          color: 'var(--text-primary, #e2e8f0)',
        }}
      >
        <input
          type="checkbox"
          checked={criticalOnly}
          onChange={(e) => onCriticalOnlyChange(e.target.checked)}
          style={{ accentColor: '#ef4444' }}
        />
        Critical only
      </label>
    </div>
  );
};

export default FilterPanel;
